import { useEffect, useRef, useState } from "react";
import { Upload, Trash2, Loader2, FileVideo, FileImage } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Creative from "@/components/Creative";

export default function AdminCreatives() {
  const [creatives, setCreatives] = useState(null);
  const [clients, setClients] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [clientId, setClientId] = useState("");
  const [campaignId, setCampaignId] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/creatives").then((r) => setCreatives(r.data)).catch(() => setCreatives([]));
  useEffect(() => {
    load();
    api.get("/admin/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/admin/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
  }, []);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("client_id", clientId);
    fd.append("campaign_id", campaignId);
    try {
      await api.post("/admin/creatives", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${file.name}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (c) => {
    try {
      await api.delete(`/admin/creatives/${c.id}`);
      toast.success("Creative deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-creatives-page">
      <div>
        <p className="section-label">Creative Assets</p>
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-white tracking-tight mt-1">Creative Library</h2>
        <p className="text-sm text-slate-400 mt-2">MP4, MOV, JPG, PNG, WEBP and GIF up to 50MB. Files are stored in secure object storage.</p>
      </div>

      <div className="card-surface p-5">
        <div className="grid sm:grid-cols-3 gap-4 items-end">
          <div>
            <Label className="text-slate-300 text-xs tracking-widest uppercase">Client</Label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger className="mt-1.5 bg-secondary/50 border-white/10" data-testid="creative-client-select"><SelectValue placeholder="Optional" /></SelectTrigger>
              <SelectContent className="bg-popover border-white/10">
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-300 text-xs tracking-widest uppercase">Campaign</Label>
            <Select value={campaignId} onValueChange={setCampaignId}>
              <SelectTrigger className="mt-1.5 bg-secondary/50 border-white/10" data-testid="creative-campaign-select"><SelectValue placeholder="Optional" /></SelectTrigger>
              <SelectContent className="bg-popover border-white/10">
                {campaigns.filter((c) => !clientId || c.client_id === clientId).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <input ref={fileRef} type="file" accept=".mp4,.mov,.jpg,.jpeg,.png,.webp,.gif" className="hidden"
              onChange={(e) => upload(e.target.files[0])} data-testid="creative-file-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="w-full bg-blue-600 hover:bg-blue-500" data-testid="upload-creative-btn">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              {uploading ? "Uploading..." : "Upload Creative"}
            </Button>
          </div>
        </div>
      </div>

      {!creatives ? (
        <Skeleton className="h-64 bg-white/5" />
      ) : creatives.length === 0 ? (
        <div className="card-surface p-14 text-center">
          <FileImage className="w-10 h-10 mx-auto text-slate-600 mb-3" />
          <p className="text-slate-400">No creatives uploaded yet.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {creatives.map((c) => (
            <div key={c.id} className="card-surface overflow-hidden group" data-testid={`creative-card-${c.id}`}>
              <div className="aspect-video bg-black/40 relative">
                <Creative url={c.url} type={c.creative_type} className="w-full h-full" testid={`creative-preview-${c.id}`} />
              </div>
              <div className="p-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{c.original_name}</p>
                  <p className="text-[11px] text-slate-500 flex items-center gap-1.5 mt-0.5">
                    {c.creative_type === "VIDEO" ? <FileVideo className="w-3 h-3" /> : <FileImage className="w-3 h-3" />}
                    {c.creative_type} · {(c.size_bytes / 1024 / 1024).toFixed(2)}MB
                  </p>
                </div>
                <Button size="sm" variant="ghost" className="h-8 text-red-400 shrink-0" onClick={() => remove(c)} data-testid={`delete-creative-${c.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
